import { useInView } from "@/hooks/useInView";
import { MapPin, Radio, ShieldCheck } from "lucide-react";

const VOIVODESHIPS = [
  "Dolnośląskie",
  "Kujawsko-Pomorskie",
  "Lubelskie",
  "Lubuskie",
  "Łódzkie",
  "Małopolskie",
  "Mazowieckie",
  "Opolskie",
  "Podkarpackie",
  "Podlaskie",
  "Pomorskie",
  "Śląskie",
  "Świętokrzyskie",
  "Warmińsko-Mazurskie",
  "Wielkopolskie",
  "Zachodniopomorskie",
];

const realizationTypes = [
  {
    schematic: "lattice65",
    label: "Maszt kratownicowy",
    height: "65 m",
    region: "Lokalizacja wiejska",
    description: "Stalowa konstrukcja kratownicowa, najczęściej spotykany typ na terenach rolnych.",
    color: "emerald",
  },
  {
    schematic: "monopole35",
    label: "Słup monopolowy",
    height: "35 m",
    region: "Strefa zabudowy",
    description: "Smukła, jednolita konstrukcja, mniej widoczna w krajobrazie miejscowości.",
    color: "amber",
  },
  {
    schematic: "rooftop6",
    label: "Maszt na dachu",
    height: "3–6 m",
    region: "Hala / budynek",
    description: "Niski maszt antenowy na obiekcie, bez wpływu na pracę zakładu.",
    color: "emerald",
  },
  {
    schematic: "premium",
    label: "Premium przy drodze",
    height: "65 m",
    region: "DK / autostrada",
    description: "Strategiczna lokalizacja dla operatorów, najwyższy potencjał Twojej działki.",
    color: "slate",
  },
];

const infoCards = [
  {
    icon: Radio,
    title: "Znajomość lokalnych procedur",
    text: "Każde województwo i gmina ma swoje specyfiki urzędowe. Nie zaczynamy od zera — znamy ścieżki administracyjne.",
    color: "emerald",
  },
  {
    icon: MapPin,
    title: "Tereny wiejskie i miejskie",
    text: "Pracujemy zarówno z rolnikami z gmin wiejskich, jak i z właścicielami obiektów przemysłowych w strefach aglomeracyjnych.",
    color: "amber",
  },
  {
    icon: ShieldCheck,
    title: "Zaufani partnerzy",
    text: "Współpracujemy z operatorami telekomunikacyjnymi w długich, wieloletnich umowach.",
    color: "slate",
  },
];

export default function Realizations() {
  const [ref, inView] = useInView(0.1);

  return (
    <section
      id="realizacje"
      className="py-24 bg-[#FAFAFA]"
      data-testid="realizations-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={ref}
          className={`text-center mb-12 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-emerald-700 text-sm font-semibold uppercase tracking-widest">
            Obszar działania
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-slate-900 mt-2 tracking-tight">
            Działamy w całej Polsce
          </h2>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            Realizujemy projekty na terenie wszystkich województw — od miejscowości wiejskich
            po obrzeża dużych aglomeracji. Niezależnie od regionu, znamy lokalne specyfiki.
          </p>
        </div>

        <VoivodeshipGrid inView={inView} />

        <div className="grid sm:grid-cols-3 gap-5 mb-20">
          {infoCards.map((card, i) => (
            <InfoCard key={card.title} card={card} index={i} />
          ))}
        </div>

        <RealizationTypes inView={inView} />
      </div>
    </section>
  );
}

function VoivodeshipGrid({ inView }) {
  return (
    <div
      className={`max-w-4xl mx-auto mb-14 transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: "150ms" }}
    >
      <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="text-5xl sm:text-6xl font-black text-emerald-700 number-highlight tabular-nums leading-none">
            16
          </span>
          <div className="text-left">
            <div className="text-xs font-bold text-emerald-700 uppercase tracking-widest">
              Województw
            </div>
            <div className="text-sm font-semibold text-slate-700">
              w obszarze naszych działań
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {VOIVODESHIPS.map((name, i) => (
            <VoivodeshipChip key={name} name={name} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </div>
  );
}

function VoivodeshipChip({ name, index, inView }) {
  return (
    <div
      className={`px-3 py-2 text-center text-xs sm:text-sm font-semibold text-slate-700 bg-emerald-50 border border-emerald-100 rounded-lg transition-all duration-500 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
      style={{ transitionDelay: `${300 + index * 30}ms` }}
    >
      {name}
    </div>
  );
}

function InfoCard({ card, index }) {
  const [ref, inView] = useInView(0.2);
  const Icon = card.icon;

  const colorMap = {
    emerald: { iconBg: "bg-emerald-100", iconColor: "text-emerald-700" },
    amber: { iconBg: "bg-amber-100", iconColor: "text-amber-700" },
    slate: { iconBg: "bg-slate-100", iconColor: "text-slate-700" },
  };
  const c = colorMap[card.color];

  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`w-10 h-10 ${c.iconBg} rounded-xl flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 ${c.iconColor}`} aria-hidden="true" />
      </div>
      <h3 className="font-bold text-slate-900 text-sm mb-2">{card.title}</h3>
      <p className="text-slate-600 text-xs leading-relaxed">{card.text}</p>
    </div>
  );
}

function RealizationTypes({ inView }) {
  return (
    <div
      className={`transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: "400ms" }}
    >
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">Typy realizacji</h3>
          <p className="text-sm text-slate-600 mt-1">
            Schematy poglądowe (skala zachowana) — co dokładnie może stanąć na Twojej działce.
          </p>
        </div>
        <p className="text-xs text-slate-500 italic max-w-xs">
          Rysunki techniczne. Wymiary podane dla typowych realizacji.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {realizationTypes.map((item, i) => (
          <TypeCard key={item.label} item={item} index={i} />
        ))}
      </div>
    </div>
  );
}

function TypeCard({ item, index }) {
  const [ref, inView] = useInView(0.2);
  const colorBg =
    item.color === "amber" ? "from-amber-50 to-white" :
    item.color === "slate" ? "from-slate-100 to-white" :
    "from-emerald-50 to-white";
  const accent =
    item.color === "amber" ? "text-amber-700 border-amber-200" :
    item.color === "slate" ? "text-slate-700 border-slate-300" :
    "text-emerald-700 border-emerald-200";

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl overflow-hidden bg-gradient-to-b ${colorBg} border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-700 flex flex-col h-full ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{ transitionDelay: `${500 + index * 80}ms` }}
    >
      <div className="aspect-[4/5] relative bg-gradient-to-b from-sky-50/40 to-white p-3 flex items-end justify-center">
        <MastSchematic type={item.schematic} color={item.color} />
      </div>
      <div className="p-4 border-t border-slate-200 bg-white">
        <div className={`text-[10px] font-bold uppercase tracking-widest ${accent} border-l-2 pl-2 mb-2`}>
          {item.region}
        </div>
        <h4 className="text-slate-900 text-sm font-bold leading-tight mb-0.5">{item.label}</h4>
        <p className="text-emerald-700 text-xs font-black tabular-nums mb-2">{item.height}</p>
        <p className="text-slate-600 text-xs leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
}

function MastSchematic({ type, color }) {
  const accentColor =
    color === "amber" ? "#B45309" :
    color === "slate" ? "#334155" :
    "#047857";
  const refColor = "#94A3B8";

  if (type === "lattice65") {
    return (
      <svg viewBox="0 0 160 220" className="w-full h-full" role="img" aria-label="Maszt kratownicowy 65 metrów, schemat skalowany w porównaniu z drzewem">
        <line x1="0" y1="200" x2="160" y2="200" stroke="#94A3B8" strokeWidth="1.5" />
        <g>
          <rect x="22" y="170" width="4" height="30" fill="#78350F" />
          <ellipse cx="24" cy="172" rx="14" ry="20" fill="#22C55E" opacity="0.8" />
          <text x="24" y="218" textAnchor="middle" fontSize="9" fill={refColor}>~10 m</text>
        </g>
        <g stroke={accentColor} fill="none" strokeWidth="1.5" strokeLinecap="round">
          <line x1="95" y1="200" x2="100" y2="20" />
          <line x1="125" y1="200" x2="120" y2="20" />
          {Array.from({ length: 12 }).map((_, i) => {
            const y = 200 - (i + 1) * 14;
            const xL = 95 + (i + 1) * (5 / 13);
            const xR = 125 - (i + 1) * (5 / 13);
            return <line key={i} x1={xL} y1={y} x2={xR} y2={y} />;
          })}
          {Array.from({ length: 12 }).map((_, i) => {
            const y1 = 200 - i * 14;
            const y2 = 200 - (i + 1) * 14;
            const xL1 = 95 + i * (5 / 13);
            const xR1 = 125 - i * (5 / 13);
            const xL2 = 95 + (i + 1) * (5 / 13);
            const xR2 = 125 - (i + 1) * (5 / 13);
            return (
              <g key={`d${i}`}>
                <line x1={xL1} y1={y1} x2={xR2} y2={y2} />
                <line x1={xR1} y1={y1} x2={xL2} y2={y2} />
              </g>
            );
          })}
        </g>
        <g fill={accentColor}>
          <rect x="100" y="22" width="3" height="14" />
          <rect x="117" y="22" width="3" height="14" />
          <rect x="108" y="14" width="3" height="14" />
          <circle cx="110" cy="10" r="3" />
        </g>
        <text x="135" y="115" fontSize="11" fontWeight="700" fill={accentColor}>65 m</text>
      </svg>
    );
  }

  if (type === "monopole35") {
    return (
      <svg viewBox="0 0 160 220" className="w-full h-full" role="img" aria-label="Słup monopolowy 35 metrów, schemat skalowany w porównaniu z domem">
        <line x1="0" y1="200" x2="160" y2="200" stroke="#94A3B8" strokeWidth="1.5" />
        <g fill={refColor} stroke={refColor} strokeWidth="1">
          <rect x="15" y="155" width="40" height="45" fill="#CBD5E1" />
          <polygon points="13,155 35,135 57,155" fill="#94A3B8" />
          <rect x="22" y="170" width="8" height="14" fill="#E2E8F0" />
          <rect x="40" y="170" width="8" height="14" fill="#E2E8F0" />
          <text x="35" y="218" textAnchor="middle" fontSize="9" fill={refColor}>~10 m</text>
        </g>
        <g fill={accentColor}>
          <polygon points="105,200 109,200 110,40 104,40" />
        </g>
        <g fill={accentColor}>
          <rect x="100" y="42" width="14" height="4" />
          <rect x="100" y="50" width="14" height="4" />
          <rect x="105" y="32" width="4" height="12" />
          <circle cx="107" cy="28" r="3" />
        </g>
        <text x="120" y="120" fontSize="11" fontWeight="700" fill={accentColor}>35 m</text>
      </svg>
    );
  }

  if (type === "rooftop6") {
    return (
      <svg viewBox="0 0 160 220" className="w-full h-full" role="img" aria-label="Maszt antenowy na dachu 3–6 metrów, schemat skalowany w porównaniu z budynkiem">
        <line x1="0" y1="200" x2="160" y2="200" stroke="#94A3B8" strokeWidth="1.5" />
        <g>
          <rect x="20" y="100" width="120" height="100" fill="#CBD5E1" stroke="#94A3B8" strokeWidth="1.5" />
          <line x1="20" y1="100" x2="140" y2="100" stroke="#64748B" strokeWidth="2" />
          {Array.from({ length: 4 }).map((_, i) => (
            <rect key={i} x={30 + i * 27} y="140" width="18" height="20" fill="#E2E8F0" stroke="#94A3B8" />
          ))}
          <rect x="70" y="170" width="20" height="30" fill="#64748B" />
          <text x="80" y="218" textAnchor="middle" fontSize="9" fill={refColor}>hala ~10 m</text>
        </g>
        <g fill={accentColor} stroke={accentColor} strokeWidth="1.5">
          <line x1="78" y1="100" x2="78" y2="60" />
          <line x1="83" y1="100" x2="83" y2="60" />
          <line x1="78" y1="80" x2="83" y2="80" />
          <line x1="78" y1="70" x2="83" y2="70" />
          <line x1="78" y1="90" x2="83" y2="90" />
          <rect x="73" y="58" width="3" height="10" />
          <rect x="85" y="58" width="3" height="10" />
          <circle cx="80.5" cy="56" r="2.5" fill={accentColor} />
        </g>
        <text x="95" y="80" fontSize="11" fontWeight="700" fill={accentColor}>3–6 m</text>
        <text x="95" y="95" fontSize="9" fill={refColor}>(maszt)</text>
      </svg>
    );
  }

  if (type === "premium") {
    return (
      <svg viewBox="0 0 160 220" className="w-full h-full" role="img" aria-label="Maszt premium przy drodze, schemat z odcinkiem drogi krajowej">
        <line x1="0" y1="200" x2="160" y2="200" stroke="#94A3B8" strokeWidth="1.5" />
        <polygon points="0,212 160,212 160,200 0,200" fill="#334155" />
        <g stroke="#FBBF24" strokeWidth="1.5" strokeDasharray="6 4">
          <line x1="0" y1="206" x2="160" y2="206" />
        </g>
        <g>
          <line x1="32" y1="200" x2="32" y2="180" stroke="#64748B" strokeWidth="1" />
          <rect x="26" y="170" width="12" height="10" rx="1" fill="#10B981" stroke="#065F46" strokeWidth="0.5" />
          <text x="32" y="220" textAnchor="middle" fontSize="8" fill={refColor}>znak ~3 m</text>
        </g>
        <g stroke={accentColor} fill="none" strokeWidth="1.5" strokeLinecap="round">
          <line x1="95" y1="200" x2="100" y2="20" />
          <line x1="125" y1="200" x2="120" y2="20" />
          {Array.from({ length: 12 }).map((_, i) => {
            const y = 200 - (i + 1) * 14;
            const xL = 95 + (i + 1) * (5 / 13);
            const xR = 125 - (i + 1) * (5 / 13);
            return <line key={i} x1={xL} y1={y} x2={xR} y2={y} />;
          })}
          {Array.from({ length: 12 }).map((_, i) => {
            const y1 = 200 - i * 14;
            const y2 = 200 - (i + 1) * 14;
            const xL1 = 95 + i * (5 / 13);
            const xR1 = 125 - i * (5 / 13);
            const xL2 = 95 + (i + 1) * (5 / 13);
            const xR2 = 125 - (i + 1) * (5 / 13);
            return (
              <g key={`d${i}`}>
                <line x1={xL1} y1={y1} x2={xR2} y2={y2} />
                <line x1={xR1} y1={y1} x2={xL2} y2={y2} />
              </g>
            );
          })}
        </g>
        <g fill={accentColor}>
          <rect x="100" y="22" width="3" height="14" />
          <rect x="117" y="22" width="3" height="14" />
          <rect x="108" y="14" width="3" height="14" />
          <circle cx="110" cy="10" r="3" />
        </g>
        <text x="135" y="115" fontSize="11" fontWeight="700" fill={accentColor}>65 m</text>
      </svg>
    );
  }

  return null;
}
